import React, { useState, type FC, useEffect } from 'react';
import cx from 'classnames';
import { ClockCircleOutlined, DownOutlined } from '@ant-design/icons';
import { Dropdown, type MenuProps } from 'antd';
import { observer } from 'mobx-react-lite';
import { Space, Text } from '@g2u/ui-kit';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { type JsonDataItem, type WithOptionalDataTest } from 'types';
import { BookingStore, RootStore } from 'store';
import styles from './customer-time.module.scss';

export namespace CustomerTime {
  export type Props = {
    isSticky?: boolean;
    isSlim?: boolean;
  } & WithOptionalDataTest;

  const stateTimezones: JsonDataItem = {
    NSW: 'Australia/Sydney',
    VIC: 'Australia/Melbourne',
    QLD: 'Australia/Brisbane',
    SA: 'Australia/Adelaide',
    WA: 'Australia/Perth',
    TAS: 'Australia/Hobart',
    NT: 'Australia/Darwin',
  };

  const timezoneDropdownOptions = Object.keys(stateTimezones).map((key) => {
    return { key, label: key };
  });

  export const $: FC<Props> = observer((props) => {
    const defaultZone = 'Australia/Sydney';
    const defaultState = 'NSW';
    const { isSticky, isSlim } = props;
    const { customer, location, job } = BookingStore;
    const { customerProfile } = RootStore;
    const [stateCode, setStateCode] = useState<string>(defaultState);
    const [timezone, setTimezone] = useState<string>(defaultZone);
    const activeCustomer = !!customer || !!customerProfile || !!location.timezone || !!job;

    const handleStatusClick = ({ key }: JsonDataItem) => {
      setStateCode(key);
      setTimezone(stateTimezones[key]);
    };

    useEffect(() => {
      // show timezone for new/returned customer booking and job rescheduling
      setTimezone(
        location?.timezone ||
          customer?.timezone ||
          customerProfile?.timezone ||
          job?.customer.timezone ||
          'Australia/Sydney',
      );
      setStateCode(
        location?.state ||
          customer?.address.state ||
          customerProfile?.address.state ||
          job?.customer.address.state ||
          'NSW',
      );
    }, [
      customer?.timezone,
      customer?.address.state,
      customerProfile?.timezone,
      customerProfile?.address.state,
      location?.state,
      location?.timezone,
      job?.customer.timezone,
      job?.customer.address.state,
    ]);

    return (
      <div
        className={cx(styles.customerTime, isSticky && styles.customerTime_sticky, isSlim && styles.customerTime_slim)}
      >
        <Dropdown
          menu={{
            items: timezoneDropdownOptions as MenuProps['items'],
            activeKey: stateCode,
            selectedKeys: [stateCode],
            selectable: true,
            onClick: handleStatusClick,
          }}
        >
          <div>
            <Space.$ direction={Space.Direction.Vertical} align={Space.Align.Center} compact>
              <Text.$ className={styles.customerTime__title}>
                {activeCustomer ? 'Customer Local Time' : 'G2U Business Time'}
              </Text.$>
              <Text.$ className={styles.customerTime__info}>
                <ClockCircleOutlined /> {format(toZonedTime(new Date(), timezone), 'HH:mm')} {stateCode}
                <DownOutlined className={styles.customerTime__arrow} />
              </Text.$>
            </Space.$>
          </div>
        </Dropdown>
      </div>
    );
  });
}
